import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api, getToken, setToken } from "./api.js";
import ImpersonationBanner from "../components/ImpersonationBanner.jsx";
import { hydrateWeekStart } from "./period.js";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | authed | anon

  // Keep the local week-start cache in sync with the account.
  useEffect(() => {
    if (user?.week_start) hydrateWeekStart(user.week_start);
  }, [user?.week_start]);

  // On boot, if we hold a token, TRUST it and proceed — then validate via /me in
  // the background. request() clears the token and throws 401 on a real expiry,
  // which is the only thing that should drop us to anon. A transient /me failure
  // (network blip, 5xx) must NOT log the user out on reload.
  useEffect(() => {
    let alive = true;
    if (!getToken()) {
      setStatus("anon");
      return undefined;
    }
    setStatus("authed");
    (async () => {
      try {
        const me = await api.me();
        if (alive) setUser(me);
      } catch (e) {
        if (alive && e?.status === 401) {
          setUser(null);
          setStatus("anon");
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const login = useCallback(async (email, password, mfaCode) => {
    await api.login(email, password, mfaCode);
    let me = null;
    try {
      me = await api.me();
    } catch (e) {
      /* /me optional; token is enough to proceed */
    }
    setUser(me);
    setStatus("authed");
  }, []);

  const register = useCallback(async (body) => {
    await api.register(body);
    let me = null;
    try {
      me = await api.me();
    } catch (e) {
      /* token is enough to proceed */
    }
    setUser(me);
    setStatus("authed");
  }, []);

  // Finish a QR sign-in: the desktop poll returned an access token (the phone
  // approved), so store it and become authed — same end state as password login.
  const completeQrLogin = useCallback(async (token) => {
    setToken(token);
    let me = null;
    try {
      me = await api.me();
    } catch (e) {
      /* token is enough to proceed */
    }
    setUser(me);
    setStatus("authed");
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
      return me;
    } catch (e) {
      return null;
    }
  }, []);

  const logout = useCallback(() => {
    api.logout();
    setUser(null);
    setStatus("anon");
  }, []);

  // Admin view-as: swap in the impersonation token, then load that user's /me.
  const startImpersonation = useCallback(async (uid) => {
    await api.impersonate(uid);
    const me = await api.me().catch(() => null);
    setUser(me);
    setStatus("authed");
    return me;
  }, []);

  const stopImpersonation = useCallback(async () => {
    await api.stopImpersonation();
    const me = await api.me().catch(() => null);
    setUser(me);
    setStatus("authed");
    return me;
  }, []);

  const demoLogin = useCallback(async () => {
    await api.demoLogin();
    const me = await api.me().catch(() => null);
    setUser(me);
    setStatus("authed");
    return me;
  }, []);

  return (
    <AuthCtx.Provider value={{ user, status, login, logout, register, refresh, completeQrLogin, startImpersonation, stopImpersonation, demoLogin }}>
      <ImpersonationBanner />
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function RequireAuth({ children, ignoreLock }) {
  const { status, user } = useAuth();
  const loc = useLocation();
  if (status === "loading")
    return <div className="center-msg">Loading…</div>;
  if (status === "anon")
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  // Trial expired → force the plan-selection screen (except the plan screen itself).
  if (!ignoreLock && user?.locked && loc.pathname !== "/plans")
    return <Navigate to="/plans" replace />;
  return children;
}
